"use client";

import { useSignals } from "@preact/signals-react/runtime";
import { motion, useReducedMotion } from "motion/react";
import { LuTriangleAlert } from "react-icons/lu";

import { subscriptionTierHealthSignal } from "@/contexts/subscription-tier-health";
import { cn } from "@/utils/lib";

export type MenuAttentionKey = "subscription-tier-health";

const animatedAttentionKeys = new Set<string>();

const hasAttentionForKey = (attentionKey?: MenuAttentionKey) => {
  if (!attentionKey) {
    return false;
  }

  switch (attentionKey) {
    case "subscription-tier-health":
      return subscriptionTierHealthSignal.value.issues.length > 0;
    default:
      return false;
  }
};

type MenuAttentionIndicatorProps = {
  attentionKey?: MenuAttentionKey;
  animationKey: string;
  className?: string;
};

export const MenuAttentionIndicator = ({
  attentionKey,
  animationKey,
  className,
}: MenuAttentionIndicatorProps) => {
  useSignals();
  const prefersReducedMotion = useReducedMotion();

  if (!hasAttentionForKey(attentionKey)) {
    return null;
  }

  const shouldAnimate =
    !prefersReducedMotion && !animatedAttentionKeys.has(animationKey);

  if (shouldAnimate) {
    animatedAttentionKeys.add(animationKey);
  }

  return (
    <motion.div
      aria-hidden="true"
      initial={shouldAnimate ? { scale: 0.2, rotate: -16, opacity: 0 } : false}
      animate={
        shouldAnimate
          ? {
              scale: [0.2, 1.36, 0.92, 1],
              rotate: [-16, 5, -2, 0],
              opacity: [0, 1, 1, 1],
            }
          : {
              scale: 1,
              rotate: 0,
              opacity: 1,
            }
      }
      transition={
        shouldAnimate
          ? {
              duration: 0.72,
              times: [0, 0.42, 0.72, 1],
              ease: "easeOut",
            }
          : { duration: 0 }
      }
      className={cn(
        `
          pointer-events-none inline-flex shrink-0 items-center justify-center
          text-destructive drop-shadow-[0_2px_8px_rgba(0,0,0,0.32)]
        `,
        className,
      )}
    >
      <LuTriangleAlert className="size-[1.45rem]" strokeWidth={3.1} />
    </motion.div>
  );
};
